import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import {
  Row,
  Col,
  Button,
  FormControl,
  Table,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap"
import Select from "react-select"
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table"
import { API_WEB_URLS } from "../../constants/constAPI"
import { useDispatch } from "react-redux"
import { Fn_FillListData } from "../../store/Functions"
import { useLocation } from "react-router-dom"
import { Container } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import ExportButtons from "../../components/ExportButtons"
import { useSignalRNotifications } from "../../context/SignalRNotificationContext"
import LiveTripTrackingModal from "../../components/CommonForBoth/LiveTripTrackingModal"
import axios from "axios"
import toastr from "toastr"
import "toastr/build/toastr.css"

const GOOGLE_MAPS_KEY = "AIzaSyBUv5ri7fIu-QtKOM-1LdVu0EozSigVQLw"

const STATUS_FILTER_OPTIONS = [
  { id: "0", name: "All Bookings" },
  { id: "100", name: "All Active / Ongoing Trips" },
  { id: "1", name: "Pending (Needs Driver)" },
  { id: "2", name: "Driver Assigned" },
  { id: "3", name: "Driver Heading to Pickup" },
  { id: "4", name: "Driver Reached Pickup" },
  { id: "5", name: "In Transit / Picked Up" },
  { id: "6", name: "Reached Drop Point" },
  { id: "18", name: "Completed / Delivered" },
  { id: "19", name: "Cancelled" },
]

const ALL_BOOKING_STATUSES = [
  {
    id: "1",
    name: "1 - Pending Assignment (Needs Driver)",
    badge: "bg-warning text-dark",
  },
  { id: "2", name: "2 - Driver Assigned", badge: "bg-primary text-white" },
  { id: "3", name: "3 - Driver Heading to Pickup", badge: "bg-info text-dark" },
  { id: "4", name: "4 - Driver Reached Pickup", badge: "bg-info text-dark" },
  {
    id: "5",
    name: "5 - Goods Loaded / In Transit",
    badge: "bg-primary text-white",
  },
  { id: "6", name: "6 - Reached Drop Location", badge: "bg-info text-dark" },
  {
    id: "18",
    name: "18 - Completed / Delivered",
    badge: "bg-success text-white",
  },
  { id: "19", name: "19 - Cancelled", badge: "bg-danger text-white" },
]

const AssignDriver = () => {
  // State management
  const [state, setState] = useState({})
  const [gridData, setGridData] = useState([])
  const [statusData, setStatusData] = useState([])
  const [driverVehicleDetails, setDriverVehicleDetails] = useState([])
  const [vehicleTypesList, setVehicleTypesList] = useState([])
  const [goodsTypesList, setGoodsTypeList] = useState([])
  const [selectedStatusId, setSelectedStatusId] = useState("0")

  // Driver Assignment Modal state
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [selectedDriverId, setSelectedDriverId] = useState("")
  const [driverSearch, setDriverSearch] = useState("")
  const [showOnlyMatchingDrivers, setShowOnlyMatchingDrivers] = useState(false)
  const [isAssigningDriver, setIsAssigningDriver] = useState(false)

  // Live Trip Tracking Modal state
  const [showLiveTrackModal, setShowLiveTrackModal] = useState(false)
  const [liveTrackBooking, setLiveTrackBooking] = useState(null)

  // Admin Photo Lightbox & Upload Modal state
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [photoBooking, setPhotoBooking] = useState(null)
  const [activePhotoTab, setActivePhotoTab] = useState("pickup")
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)

  // Admin Edit Booking Modal state
  const [showEditModal, setShowEditModal] = useState(false)
  const [editFormData, setEditFormData] = useState(null)
  const editFormDataRef = useRef(null)
  const [originalBookingState, setOriginalBookingState] = useState(null)
  const originalBookingStateRef = useRef(null)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [isRecalculatingRate, setIsRecalculatingRate] = useState(false)
  const [fareRecalcStatus, setFareRecalcStatus] = useState(null)
  const pickupSearchInputRef = useRef(null)
  const dropSearchInputRef = useRef(null)
  const [googleMapsReady, setGoogleMapsReady] = useState(false)

  useEffect(() => {
    editFormDataRef.current = editFormData
  }, [editFormData])

  useEffect(() => {
    originalBookingStateRef.current = originalBookingState
  }, [originalBookingState])

  // Dynamically load Google Maps Places API for location autocomplete
  useEffect(() => {
    if (window.google?.maps?.places) {
      setGoogleMapsReady(true)
      return
    }
    const scriptId = "google-maps-places-script"
    let script = document.getElementById(scriptId)
    if (!script) {
      script = document.createElement("script")
      script.id = scriptId
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places,geometry`
      script.async = true
      script.defer = true
      script.onload = () => setGoogleMapsReady(true)
      document.head.appendChild(script)
    } else {
      script.addEventListener("load", () => setGoogleMapsReady(true))
      if (window.google?.maps?.places) setGoogleMapsReady(true)
    }
  }, [])

  // Haversine fallback distance formula (with 1.25x road curvature factor)
  const calculateHaversineRoadKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371
    const dLat = (Number(lat2) - Number(lat1)) * (Math.PI / 180)
    const dLon = (Number(lon2) - Number(lon1)) * (Math.PI / 180)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(Number(lat1) * (Math.PI / 180)) *
        Math.cos(Number(lat2) * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return Number((R * c * 1.25).toFixed(2))
  }

  // Real Driving Road Distance via Google Directions API
  const calculateRoadDistance = (pLat, pLng, dLat, dLng) => {
    return new Promise(resolve => {
      const numPLat = parseFloat(pLat)
      const numPLng = parseFloat(pLng)
      const numDLat = parseFloat(dLat)
      const numDLng = parseFloat(dLng)

      if (
        isNaN(numPLat) ||
        isNaN(numPLng) ||
        isNaN(numDLat) ||
        isNaN(numDLng) ||
        numPLat === 0 ||
        numDLat === 0
      ) {
        return resolve(null)
      }

      if (window.google?.maps?.DirectionsService) {
        try {
          const directionsService = new window.google.maps.DirectionsService()
          directionsService.route(
            {
              origin: new window.google.maps.LatLng(numPLat, numPLng),
              destination: new window.google.maps.LatLng(numDLat, numDLng),
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (
                status === window.google.maps.DirectionsStatus.OK &&
                result?.routes?.[0]?.legs?.[0]?.distance?.value != null
              ) {
                const meters = result.routes[0].legs[0].distance.value
                const km = Number((meters / 1000).toFixed(2))
                return resolve(km > 0 ? km : 0.1)
              }
              return resolve(
                calculateHaversineRoadKm(numPLat, numPLng, numDLat, numDLng)
              )
            }
          )
          return
        } catch (e) {
          console.warn("DirectionsService routing error:", e)
        }
      }
      return resolve(
        calculateHaversineRoadKm(numPLat, numPLng, numDLat, numDLng)
      )
    })
  }

  // Policy:
  // - If distance <= original distance: NO FARE CHANGE (keep original fare)
  // - If distance > original distance: RE-CALCULATE FARE via RateCalculation API
  const triggerRouteDistanceAndFareCheck = useCallback(
    async (pLat, pLng, dLat, dLng, overrideVehicleType = null) => {
      const orig = originalBookingStateRef.current
      if (!orig) return

      const origKm = Number(orig.TotalTripKM || 0)
      const origPrice = Number(orig.Price || 0)

      setIsRecalculatingRate(true)
      setFareRecalcStatus({
        type: "calculating",
        message: "Calculating road distance between updated locations...",
      })

      const newKm = await calculateRoadDistance(pLat, pLng, dLat, dLng)
      if (!newKm || newKm <= 0) {
        setIsRecalculatingRate(false)
        setFareRecalcStatus(null)
        return
      }

      // Rule 1: Agar distance kam ya same ho (newKm <= origKm), koi change nahi fare me
      if (newKm <= origKm) {
        setEditFormData(prev => ({
          ...prev,
          TotalTripKM: newKm,
          Price: origPrice, // Retain original fare
        }))
        setFareRecalcStatus({
          type: "info",
          message: `Distance changed to ${newKm} KM (≤ original ${origKm} KM). As per policy, fare remains unchanged at ₹${origPrice}.`,
          newKm,
          origKm,
          fare: origPrice,
          isIncreased: false,
        })
        setIsRecalculatingRate(false)
        return
      }

      // Rule 2: Agar distance increase ho (newKm > origKm), fare recalculate hoga
      setFareRecalcStatus({
        type: "calculating",
        message: `Distance increased from ${origKm} KM to ${newKm} KM (+${(
          newKm - origKm
        ).toFixed(2)} KM). Fetching updated system rate...`,
      })

      try {
        const currentForm = editFormDataRef.current || {}
        const vType =
          overrideVehicleType ||
          currentForm.F_VehicleType ||
          orig.F_VehicleType ||
          0
        const vCategory =
          currentForm.F_VehicleCategory || orig.F_VehicleCategory || 0

        const vFormData = new FormData()
        vFormData.append("KM", newKm)
        vFormData.append("F_VehicleCategory", vCategory)
        vFormData.append("F_VehicleType", vType)
        vFormData.append("F_BookingMaster", 0)
        vFormData.append("F_UserTypeMaster", 3)

        const res = await axios.post(
          `${API_WEB_URLS.BASE}RateCalculation/0/token`,
          vFormData
        )
        const rateList = res.data?.data?.response

        let calculatedFare = origPrice
        if (Array.isArray(rateList) && rateList.length > 0) {
          const match =
            rateList.find(r => String(r.F_VehicleType) === String(vType)) ||
            rateList[0]
          if (match) {
            calculatedFare = Number(
              match.TotalFare || match.BaseFare || origPrice
            )
          }
        }

        // Safeguard: even if calculated base fare is lower, never drop below original fare
        if (calculatedFare < origPrice) {
          calculatedFare = origPrice
        }

        setEditFormData(prev => ({
          ...prev,
          TotalTripKM: newKm,
          Price: calculatedFare,
        }))

        setFareRecalcStatus({
          type: "success",
          message: `Distance increased from ${origKm} KM to ${newKm} KM (+${(
            newKm - origKm
          ).toFixed(
            2
          )} KM). Fare recalculated: ₹${origPrice} ➔ ₹${calculatedFare}.`,
          newKm,
          origKm,
          fare: calculatedFare,
          isIncreased: true,
        })
        toastr.success(
          `Distance increased (${newKm} KM). Fare recalculated to ₹${calculatedFare}!`
        )
      } catch (err) {
        console.error("Fare recalculation error:", err)
        setEditFormData(prev => ({
          ...prev,
          TotalTripKM: newKm,
        }))
        setFareRecalcStatus({
          type: "warning",
          message: `Distance increased to ${newKm} KM (+${(
            newKm - origKm
          ).toFixed(
            2
          )} KM). Could not fetch automatic rate; please verify fare manually.`,
          newKm,
          origKm,
          fare: editFormDataRef.current?.Price || origPrice,
          isIncreased: true,
        })
      } finally {
        setIsRecalculatingRate(false)
      }
    },
    []
  )

  // Initialize Autocomplete listeners on Pickup and Drop search inputs when Edit Modal opens
  useEffect(() => {
    if (!showEditModal || !googleMapsReady || !window.google?.maps?.places)
      return

    const timer = setTimeout(() => {
      if (pickupSearchInputRef.current) {
        try {
          const pickupAutocomplete = new window.google.maps.places.Autocomplete(
            pickupSearchInputRef.current,
            {
              componentRestrictions: { country: "in" },
              fields: ["geometry", "name", "formatted_address"],
            }
          )
          pickupAutocomplete.addListener("place_changed", () => {
            const place = pickupAutocomplete.getPlace()
            if (!place || !place.geometry?.location) return
            const lat = place.geometry.location.lat().toFixed(7)
            const lng = place.geometry.location.lng().toFixed(7)
            const addr = place.formatted_address || place.name || ""
            setEditFormData(prev => ({
              ...prev,
              PickupAddress: addr,
              PickupLatitude: lat,
              PickupLongitude: lng,
            }))
            toastr.success(`Pickup location set: ${lat}, ${lng}`)

            // Trigger distance & fare check if drop location is available
            const currentDropLat = editFormDataRef.current?.Stop1Latitude
            const currentDropLng = editFormDataRef.current?.Stop1Longitude
            if (currentDropLat && currentDropLng) {
              triggerRouteDistanceAndFareCheck(
                lat,
                lng,
                currentDropLat,
                currentDropLng
              )
            }
          })
        } catch (err) {
          console.warn("Pickup Autocomplete init error:", err)
        }
      }

      if (dropSearchInputRef.current) {
        try {
          const dropAutocomplete = new window.google.maps.places.Autocomplete(
            dropSearchInputRef.current,
            {
              componentRestrictions: { country: "in" },
              fields: ["geometry", "name", "formatted_address"],
            }
          )
          dropAutocomplete.addListener("place_changed", () => {
            const place = dropAutocomplete.getPlace()
            if (!place || !place.geometry?.location) return
            const lat = place.geometry.location.lat().toFixed(7)
            const lng = place.geometry.location.lng().toFixed(7)
            const addr = place.formatted_address || place.name || ""
            setEditFormData(prev => ({
              ...prev,
              Stop1Address: addr,
              Stop1Latitude: lat,
              Stop1Longitude: lng,
            }))
            toastr.success(`Drop location set: ${lat}, ${lng}`)

            // Trigger distance & fare check if pickup location is available
            const currentPickLat = editFormDataRef.current?.PickupLatitude
            const currentPickLng = editFormDataRef.current?.PickupLongitude
            if (currentPickLat && currentPickLng) {
              triggerRouteDistanceAndFareCheck(
                currentPickLat,
                currentPickLng,
                lat,
                lng
              )
            }
          })
        } catch (err) {
          console.warn("Drop Autocomplete init error:", err)
        }
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [showEditModal, googleMapsReady, triggerRouteDistanceAndFareCheck])

  // Admin Quick Status Changer Modal state
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusBooking, setStatusBooking] = useState(null)
  const [newStatusId, setNewStatusId] = useState("")
  const [statusReason, setStatusReason] = useState("")
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  const [loading, setLoading] = useState(true)
  const dispatch = useDispatch()
  const location = useLocation()
  const { subscribeToNewBookings, isConnected } = useSignalRNotifications()
  const [newlyAddedId, setNewlyAddedId] = useState(null)
  const breadCrumbTitle = "Booking Report"
  const breadcrumbItem = "Booking Report"

  const API_URL = `${API_WEB_URLS.MASTER}/0/token/BookingMaster`
  const API_CANCEL_URL = `${API_WEB_URLS.BASE}Masters/0/token/CancelBookingMaster`
  const API_URL1 = `${API_WEB_URLS.MASTER}/0/token/StatusMaster`
  const API_URL2 = `${API_WEB_URLS.MASTER}/0/token/DriverVehicleDetails`
  const API_URL3 = `${API_WEB_URLS.BASE}Masters/0/token/UpdateVehicleMaster`

  const fetchTableData = useCallback(
    async ID => {
      await Fn_FillListData(
        dispatch,
        setGridData,
        "gridData",
        `${API_URL}/Id/${ID}`
      )
    },
    [dispatch, API_URL]
  )

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        await fetchTableData("0")
        await Fn_FillListData(
          dispatch,
          setStatusData,
          "gridData",
          `${API_URL1}/Id/0`
        )
        await Fn_FillListData(
          dispatch,
          setDriverVehicleDetails,
          "gridData",
          `${API_URL2}/Id/0`
        )

        // Load vehicle types list for Edit modal dropdown
        try {
          const vtRes = await axios.get(
            `${API_WEB_URLS.BASE}Masters/0/token/VehicleType/Id/0`
          )
          if (vtRes.data?.data?.dataList) {
            setVehicleTypesList(vtRes.data.data.dataList)
          }
        } catch (vtErr) {
          console.error("Failed to load vehicle types:", vtErr)
        }

        // Load Goods Types list for Edit modal datalist
        try {
          const gtRes = await axios.get(
            `${API_WEB_URLS.BASE}Masters/0/token/GoodsTypeMaster/Id/0`
          )
          if (gtRes.data?.data?.dataList) {
            setGoodsTypeList(gtRes.data.data.dataList)
          }
        } catch (gtErr) {
          console.error("Failed to load Goods Types:", gtErr)
        }

        setSelectedStatusId("0")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [dispatch, API_URL, API_URL1, API_URL2, fetchTableData])

  const driverOptions = useMemo(
    () => driverVehicleDetails || [],
    [driverVehicleDetails]
  )
  const selectedDriverDetails = useMemo(() => {
    if (!selectedDriverId) {
      return undefined
    }
    return driverOptions.find(
      driver => String(driver.Id) === String(selectedDriverId)
    )
  }, [driverOptions, selectedDriverId])

  const matchingDriversCount = useMemo(() => {
    if (!selectedBooking || !driverOptions.length) return 0
    const demandedType = Number(selectedBooking.F_VehicleType || 0)
    if (!demandedType) return 0
    return driverOptions.filter(d => Number(d.F_VehicleType) === demandedType)
      .length
  }, [selectedBooking, driverOptions])

  // React-Select custom styling for modal integration
  const reactSelectCustomStyles = useMemo(
    () => ({
      menuPortal: base => ({ ...base, zIndex: 99999 }),
      menu: base => ({ ...base, zIndex: 99999 }),
      control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
        boxShadow: state.isFocused
          ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
          : "none",
        "&:hover": {
          borderColor: "#86b7fe",
        },
        minHeight: "44px",
        borderRadius: "0.375rem",
        fontSize: "0.92rem",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "#0d6efd"
          : state.isFocused
          ? "#e8f2fc"
          : "#fff",
        color: state.isSelected ? "#fff" : "#212529",
        cursor: "pointer",
        padding: "8px 12px",
      }),
    }),
    []
  )

  // React-Select Options for Drivers & Vehicles
  const reactSelectDriverOptions = useMemo(() => {
    if (!driverOptions || !driverOptions.length) return []
    const demandedType = selectedBooking
      ? Number(selectedBooking.F_VehicleType || 0)
      : 0
    const currentVehId = selectedBooking
      ? String(selectedBooking.F_VehicleMaster || "")
      : ""

    const sorted = [...driverOptions].sort((a, b) => {
      // 1. Currently assigned driver on top
      const aIsCurrent = currentVehId && String(a.Id) === currentVehId ? 1 : 0
      const bIsCurrent = currentVehId && String(b.Id) === currentVehId ? 1 : 0
      if (aIsCurrent !== bIsCurrent) return bIsCurrent - aIsCurrent

      // 2. Matching vehicle type second
      const aMatch =
        demandedType > 0 && Number(a.F_VehicleType) === demandedType ? 1 : 0
      const bMatch =
        demandedType > 0 && Number(b.F_VehicleType) === demandedType ? 1 : 0
      if (aMatch !== bMatch) return bMatch - aMatch

      // 3. Alphabetical by DriverName
      return (a.DriverName || "").localeCompare(b.DriverName || "")
    })

    const driverOpts = sorted.map(d => {
      const plate =
        d.VehicleNo?.trim() || d.VehicleRegistrationNo?.trim() || "No Plate"
      const isCurrent = Boolean(currentVehId && String(d.Id) === currentVehId)
      const isMatch = Boolean(
        !isCurrent &&
          demandedType > 0 &&
          Number(d.F_VehicleType) === demandedType
      )

      const currentPrefix = isCurrent ? "🔒 [CURRENT ASSIGNED] " : ""
      const matchPrefix = isMatch ? "⭐ [RECOMMENDED MATCH] " : ""

      return {
        value: String(d.Id),
        label: `${currentPrefix}${matchPrefix}[${plate}] ${
          d.DriverName || "Unknown"
        } · 📞 ${d.DriverMobileNo || "-"} · 🚛 ${
          d.VehicleTypeName || "Vehicle"
        } (${d.WeightCapacity || "-"} kg)`,
        driver: d,
        isCurrentAssigned: isCurrent,
        isMatch: isMatch,
        plate: plate,
        driverName: d.DriverName || "Unknown",
        mobile: d.DriverMobileNo || "-",
        vehicleType: d.VehicleTypeName || "Vehicle",
        capacity: d.WeightCapacity || "-",
      }
    })

    return [
      { value: "", label: "-- Select Driver & Vehicle --", isDefault: true },
      ...driverOpts,
    ]
  }, [driverOptions, selectedBooking])

  const selectedDriverOption = useMemo(() => {
    if (!selectedDriverId) {
      return reactSelectDriverOptions[0] || null
    }
    return (
      reactSelectDriverOptions.find(
        opt => String(opt.value) === String(selectedDriverId)
      ) || null
    )
  }, [reactSelectDriverOptions, selectedDriverId])

  // PHOTO RESOLUTION & IMAGE BUILD HELPERS
  const buildImageUrl = imageName => {
    if (!imageName) return ""
    if (typeof imageName !== "string") return ""
    const cleanName = imageName.trim()
    if (cleanName.startsWith("http://") || cleanName.startsWith("https://"))
      return cleanName
    return `${API_WEB_URLS.IMAGEURL}${cleanName}`
  }

  const getPickupPhoto = booking => {
    if (!booking) return null
    return (
      booking.GoodsPhotoByDriver ||
      booking.GoodsPhotoByCustomer ||
      booking.PickupPhoto ||
      (Number(booking.F_StatusMaster) < 6 ? booking.Stop1Photo : null) ||
      (booking.Stop1Photo &&
      (booking.Stop1Photo.toLowerCase().includes("pickup") ||
        booking.Stop1Photo.toLowerCase().includes("goods"))
        ? booking.Stop1Photo
        : null) ||
      null
    )
  }

  const getDropPhoto = booking => {
    if (!booking) return null
    return (
      booking.DropPhoto ||
      booking.DeliveryPhoto ||
      (Number(booking.F_StatusMaster) >= 6 ? booking.Stop1Photo : null) ||
      booking.Stop4Photo ||
      booking.Stop3Photo ||
      booking.Stop2Photo ||
      null
    )
  }

  // ASSIGN / REASSIGN DRIVER ACTIONS
  const btnAssignDriverOnClick = useCallback(
    booking => {
      setSelectedBooking(booking)

      const demandedType = Number(booking.F_VehicleType || 0)

      // If booking already has assigned vehicle, pre-select it
      if (booking.F_VehicleMaster && Number(booking.F_VehicleMaster) > 0) {
        setSelectedDriverId(String(booking.F_VehicleMaster))
      } else {
        const matchingDriver = driverOptions.find(
          d => Number(d.F_VehicleType) === demandedType
        )
        const defaultDriverId = matchingDriver ? String(matchingDriver.Id) : ""
        setSelectedDriverId(defaultDriverId)
      }
      setShowAssignModal(true)
    },
    [driverOptions]
  )

  const handleAssignModalClose = useCallback(() => {
    setShowAssignModal(false)
    setSelectedBooking(null)
    setSelectedDriverId("")
  }, [])

  const handleAssignConfirm = useCallback(async () => {
    if (!selectedBooking || !selectedDriverId) {
      toastr.warning("Please select a driver & vehicle to assign.")
      return
    }

    const currentAssignedVehicleId = selectedBooking.F_VehicleMaster
      ? String(selectedBooking.F_VehicleMaster)
      : ""
    const isReassign = Boolean(
      currentAssignedVehicleId && Number(currentAssignedVehicleId) > 0
    )

    // If reassigning and the same driver is selected without changes, prompt admin
    if (isReassign && currentAssignedVehicleId === String(selectedDriverId)) {
      if (
        !window.confirm(
          `Booking #${selectedBooking.Id} is already assigned to this driver (${
            selectedDriverDetails?.DriverName || "Vehicle"
          }). Do you want to re-dispatch/re-confirm assignment for the same driver?`
        )
      ) {
        return
      }
    }

    setIsAssigningDriver(true)
    try {
      const res = await axios.get(
        `${API_URL3}/${selectedBooking.Id}/${selectedDriverId}`
      )
      if (res.data?.success || res.status === 200) {
        const updatedBooking = res.data?.data?.dataList?.[0]

        if (updatedBooking && Number(updatedBooking.Id) === -1) {
          toastr.error(
            "Reassignment rejected by server: Vehicle already assigned or invalid booking."
          )
          return
        }

        if (updatedBooking && Number(updatedBooking.Id) > 0) {
          const newDriverName =
            updatedBooking.DriverName ||
            selectedDriverDetails?.DriverName ||
            "Driver"
          const newVehPlate =
            updatedBooking.VehicleNo ||
            selectedDriverDetails?.VehicleNo ||
            "Vehicle"
          toastr.success(
            isReassign
              ? `Driver successfully reassigned to ${newDriverName} (${newVehPlate}) for Booking #${selectedBooking.Id}!`
              : `Driver ${newDriverName} (${newVehPlate}) assigned successfully to Booking #${selectedBooking.Id}!`
          )

          // Instantly update the local row in table grid
          setGridData(prev =>
            prev.map(item =>
              String(item.Id) === String(updatedBooking.Id)
                ? { ...item, ...updatedBooking }
                : item
            )
          )

          // Close modal immediately so UI is responsive
          setShowAssignModal(false)
          setSelectedBooking(null)
          setSelectedDriverId("")

          // Refresh table data
          if (
            selectedStatusId !== "0" &&
            selectedStatusId !== "100" &&
            selectedStatusId !== "2"
          ) {
            setSelectedStatusId("0")
            await fetchTableData("0")
          } else {
            await fetchTableData(selectedStatusId)
          }
        } else {
          toastr.error(
            "Assignment response was invalid. Please refresh the page."
          )
        }
      } else {
        toastr.error(res.data?.message || "Failed to assign driver.")
      }
    } catch (err) {
      console.error("Assign driver error:", err)
      toastr.error(
        "Error occurred while assigning driver: " +
          (err.response?.data?.message || err.message)
      )
    } finally {
      setIsAssigningDriver(false)
    }
  }, [
    selectedBooking,
    selectedDriverId,
    selectedDriverDetails,
    API_URL3,
    selectedStatusId,
    fetchTableData,
  ])

  // CANCEL BOOKING ACTION
  const btnCancelOnClick = useCallback(
    async booking => {
      if (
        !window.confirm(
          `Are you sure you want to cancel Booking #${booking.Id}?`
        )
      ) {
        return
      }
      try {
        await axios.get(`${API_CANCEL_URL}/Id/${booking.Id}`)
        toastr.success(`Booking #${booking.Id} cancelled successfully!`)
        await fetchTableData(selectedStatusId)
      } catch (err) {
        console.error("Cancel booking error:", err)
        toastr.error("Failed to cancel booking")
      }
    },
    [API_CANCEL_URL, fetchTableData, selectedStatusId]
  )

  // PHOTO LIGHTBOX & UPLOAD ACTIONS
  const handleOpenPhotoModal = (booking, initialTab = "pickup") => {
    setPhotoBooking(booking)
    setActivePhotoTab(initialTab)
    setShowPhotoModal(true)
  }

  const handleUploadPhoto = async (type, file) => {
    if (!photoBooking || !file) return
    setIsUploadingPhoto(true)
    try {
      const formData = new FormData()
      formData.append("Id", photoBooking.Id)
      const status = type === "pickup" ? 5 : 8
      formData.append("F_StatusMaster", photoBooking.F_StatusMaster || status)

      if (type === "pickup") {
        formData.append("GoodsPhotoByDriver", file)
      } else {
        formData.append("DropPhoto", file)
        formData.append("Stop1Photo", file)
      }

      const res = await axios.post(
        `${API_WEB_URLS.BASE}UpdateStatusWithPhoto/0/token`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      )

      if (res.data?.success || res.status === 200) {
        toastr.success(
          `${type === "pickup" ? "Pickup" : "Drop"} photo updated successfully!`
        )
        await fetchTableData(selectedStatusId)

        // Fetch refreshed record to update modal immediately
        try {
          const updatedResp = await axios.get(
            `${API_WEB_URLS.BASE}Masters/0/token/BookingMaster/Id/${photoBooking.Id}`
          )
          if (updatedResp.data?.data?.dataList?.[0]) {
            setPhotoBooking(updatedResp.data.data.dataList[0])
          }
        } catch (e) {
          console.warn("Could not reload modal booking:", e)
        }
      } else {
        toastr.error(res.data?.message || "Failed to update photo")
      }
    } catch (err) {
      console.error("Photo upload error:", err)
      toastr.error(err.message || "Failed to upload photo")
    } finally {
      setIsUploadingPhoto(false)
    }
  }

  // EDIT BOOKING ACTIONS (ADMIN FULL CONTROL)
  const handleOpenEditModal = booking => {
    setOriginalBookingState({
      Id: booking.Id,
      TotalTripKM: Number(booking.TotalTripKM || 0),
      Price: Number(booking.Price || 0),
      PickupLatitude: booking.PickupLatitude,
      PickupLongitude: booking.PickupLongitude,
      Stop1Latitude: booking.Stop1Latitude,
      Stop1Longitude: booking.Stop1Longitude,
      F_VehicleType: booking.F_VehicleType,
      F_VehicleCategory: booking.F_VehicleCategory,
    })
    setFareRecalcStatus(null)
    setEditFormData({
      Id: booking.Id,
      Price: booking.Price ?? "",
      Discount: booking.Discount ?? 0,
      TotalTripKM: booking.TotalTripKM ?? "",
      F_VehicleType: booking.F_VehicleType ? String(booking.F_VehicleType) : "",
      F_VehicleCategory: booking.F_VehicleCategory
        ? String(booking.F_VehicleCategory)
        : "",
      GoodsType: booking.GoodsType ?? "",
      ItemDetails: booking.ItemDetails ?? "",
      ItemWeight: booking.ItemWeight ?? "",
      PickupAddress: booking.PickupAddress ?? "",
      PickupLatitude: booking.PickupLatitude ?? "",
      PickupLongitude: booking.PickupLongitude ?? "",
      SenderName: booking.SenderName ?? "",
      SenderMobile: booking.SenderMobile ?? "",
      Stop1Address: booking.Stop1Address ?? "",
      Stop1Latitude: booking.Stop1Latitude ?? "",
      Stop1Longitude: booking.Stop1Longitude ?? "",
      Stop1ReceiverName: booking.Stop1ReceiverName ?? "",
      Stop1ReceiverMobile: booking.Stop1ReceiverMobile ?? "",
      Status: booking.Status,
      F_StatusMaster: booking.F_StatusMaster,
    })
    setShowEditModal(true)
  }

  const handleDetectCurrentLocation = (target = "pickup") => {
    if (!navigator.geolocation) {
      toastr.warning("Geolocation is not supported by your browser")
      return
    }
    toastr.info("Detecting device GPS coordinates...")
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude.toFixed(7)
        const lng = pos.coords.longitude.toFixed(7)
        if (target === "pickup") {
          setEditFormData(prev => ({
            ...prev,
            PickupLatitude: lat,
            PickupLongitude: lng,
          }))
          toastr.success(`Pickup coordinates set from your GPS: ${lat}, ${lng}`)
          const currentDropLat = editFormDataRef.current?.Stop1Latitude
          const currentDropLng = editFormDataRef.current?.Stop1Longitude
          if (currentDropLat && currentDropLng) {
            triggerRouteDistanceAndFareCheck(
              lat,
              lng,
              currentDropLat,
              currentDropLng
            )
          }
        } else {
          setEditFormData(prev => ({
            ...prev,
            Stop1Latitude: lat,
            Stop1Longitude: lng,
          }))
          toastr.success(`Drop coordinates set from your GPS: ${lat}, ${lng}`)
          const currentPickLat = editFormDataRef.current?.PickupLatitude
          const currentPickLng = editFormDataRef.current?.PickupLongitude
          if (currentPickLat && currentPickLng) {
            triggerRouteDistanceAndFareCheck(
              currentPickLat,
              currentPickLng,
              lat,
              lng
            )
          }
        }
      },
      err => {
        toastr.error(`Could not detect location: ${err.message}`)
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const handleSaveEditBooking = async e => {
    e.preventDefault()
    if (!editFormData?.Id) return
    setIsSavingEdit(true)
    try {
      const formData = new FormData()
      formData.append("Id", editFormData.Id)
      formData.append("Price", editFormData.Price || 0)
      formData.append("Discount", editFormData.Discount || 0)
      formData.append("TotalTripKM", editFormData.TotalTripKM || 0)
      if (editFormData.F_VehicleType) {
        formData.append("F_VehicleType", editFormData.F_VehicleType)
      }
      if (editFormData.F_VehicleCategory) {
        formData.append("F_VehicleCategory", editFormData.F_VehicleCategory)
      }
      formData.append("GoodsType", editFormData.GoodsType || "")
      formData.append("ItemDetails", editFormData.ItemDetails || "")
      formData.append("ItemWeight", editFormData.ItemWeight || "")
      formData.append("PickupAddress", editFormData.PickupAddress || "")
      formData.append("PickupLatitude", editFormData.PickupLatitude || "")
      formData.append("PickupLongitude", editFormData.PickupLongitude || "")
      formData.append("SenderName", editFormData.SenderName || "")
      formData.append("SenderMobile", editFormData.SenderMobile || "")
      formData.append("Stop1Address", editFormData.Stop1Address || "")
      formData.append("Stop1Latitude", editFormData.Stop1Latitude || "")
      formData.append("Stop1Longitude", editFormData.Stop1Longitude || "")
      formData.append("Stop1ReceiverName", editFormData.Stop1ReceiverName || "")
      formData.append(
        "Stop1ReceiverMobile",
        editFormData.Stop1ReceiverMobile || ""
      )

      const res = await axios.put(
        `${API_WEB_URLS.BASE}BookingMaster/0/token/${editFormData.Id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )

      if (res.data?.success || res.status === 200) {
        toastr.success(
          `Booking #${editFormData.Id} updated successfully with location & coordinates!`
        )
        setShowEditModal(false)
        setEditFormData(null)
        await fetchTableData(selectedStatusId)
      } else {
        toastr.error(res.data?.message || "Failed to update booking")
      }
    } catch (err) {
      console.error("Save edit error:", err)
      toastr.error(err.message || "Failed to save booking changes")
    } finally {
      setIsSavingEdit(false)
    }
  }

  // QUICK STATUS CHANGE ACTIONS (ADMIN OVERRIDE)
  const handleOpenStatusModal = booking => {
    setStatusBooking(booking)
    setNewStatusId(String(booking.F_StatusMaster || "1"))
    setStatusReason("")
    setShowStatusModal(true)
  }

  const handleConfirmStatusChange = async () => {
    if (!statusBooking || !newStatusId) return
    setIsUpdatingStatus(true)
    try {
      if (newStatusId === "19") {
        await axios.get(`${API_CANCEL_URL}/Id/${statusBooking.Id}`)
        toastr.success(`Booking #${statusBooking.Id} marked as Cancelled`)
      } else {
        await axios.get(
          `${API_WEB_URLS.BASE}Masters/0/token/UpdateStatusByDriver/${statusBooking.Id}/${newStatusId}`
        )
        toastr.success(
          `Booking #${statusBooking.Id} status updated successfully`
        )
      }
      setShowStatusModal(false)
      setStatusBooking(null)
      await fetchTableData(selectedStatusId)
    } catch (err) {
      console.error("Status change error:", err)
      toastr.error(err.message || "Failed to update status")
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleStatusFilterChange = useCallback(
    async event => {
      const { value } = event.target
      setSelectedStatusId(value)
      setLoading(true)
      try {
        await fetchTableData(value)
      } finally {
        setLoading(false)
      }
    },
    [fetchTableData]
  )

  const data = useMemo(() => {
    if (!gridData || !Array.isArray(gridData)) return []
    const seen = new Set()
    const unique = []
    for (const item of gridData) {
      if (item && item.Id != null) {
        const idKey = String(item.Id)
        if (!seen.has(idKey)) {
          seen.add(idKey)
          unique.push(item)
        }
      }
    }
    return unique.sort((a, b) => Number(b.Id || 0) - Number(a.Id || 0))
  }, [gridData])

  // Subscribe to real-time SignalR bookings for live auto-refresh
  useEffect(() => {
    const unsubscribe = subscribeToNewBookings(newBooking => {
      console.log(
        "⚡ [AssignDriver] Live booking received via SignalR. Refreshing grid...",
        newBooking
      )
      fetchTableData(selectedStatusId || "0")
      if (newBooking?.BookingId) {
        setNewlyAddedId(String(newBooking.BookingId))
        setTimeout(() => setNewlyAddedId(null), 10000)
      }
    })
    return () => unsubscribe()
  }, [subscribeToNewBookings, fetchTableData, selectedStatusId])

  // When arriving from topbar notification click, highlight booking and open assign modal if available
  useEffect(() => {
    if (location.state?.highlightBookingId && gridData && gridData.length > 0) {
      const targetId = String(location.state.highlightBookingId)
      setNewlyAddedId(targetId)
      const match = gridData.find(b => String(b.Id) === targetId)
      if (match) {
        btnAssignDriverOnClick(match)
      }
    }
  }, [location.state, gridData, btnAssignDriverOnClick])

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "Id",
        Cell: ({ row, value }) => {
          const raw =
            row.original?.DateOfCreation ||
            row.original?.dateOfCreation ||
            row.original?.BookingDate ||
            row.original?.bookingDate ||
            row.original?.CreatedDate ||
            row.original?.createdDate ||
            row.original?.LastUpdateOn ||
            row.original?.lastUpdateOn
          let timeDisplay = null
          if (raw) {
            try {
              const d = new Date(raw)
              if (!isNaN(d.getTime())) {
                const dateStr = d.toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
                const timeStr = d.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
                timeDisplay = (
                  <div
                    className="mt-1 text-nowrap"
                    style={{ fontSize: "0.74rem", lineHeight: 1.2 }}
                  >
                    <span className="fw-semibold text-primary d-block">
                      <i className="far fa-clock me-1"></i>
                      {timeStr}
                    </span>
                    <span className="text-muted small">{dateStr}</span>
                  </div>
                )
              }
            } catch (e) {}
          }
          return (
            <div>
              <span className="text-muted small fw-bold font-monospace">
                #{value || "-"}
              </span>
              {timeDisplay}
            </div>
          )
        },
      },
      {
        Header: "Time of Booking Done",
        accessor: "DateOfCreation",
        Cell: ({ row }) => {
          const raw =
            row.original?.DateOfCreation ||
            row.original?.dateOfCreation ||
            row.original?.BookingDate ||
            row.original?.bookingDate ||
            row.original?.CreatedDate ||
            row.original?.createdDate ||
            row.original?.LastUpdateOn ||
            row.original?.lastUpdateOn
          if (!raw) return <span className="text-muted small">N/A</span>
          try {
            const d = new Date(raw)
            if (isNaN(d.getTime()))
              return <span className="text-muted small">{String(raw)}</span>
            const dateStr = d.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            const timeStr = d.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
            return (
              <div className="text-nowrap" style={{ fontSize: "0.78rem" }}>
                <span className="fw-semibold text-primary d-block">
                  <i className="far fa-clock me-1"></i>
                  {timeStr}
                </span>
                <span className="text-muted small">{dateStr}</span>
              </div>
            )
          } catch {
            return <span className="text-muted small">{String(raw)}</span>
          }
        },
      },
      {
        Header: "Pickup Location & Photo",
        accessor: "PickupAddress",
        Cell: ({ row }) => {
          const pickupImg = getPickupPhoto(row.original)
          return (
            <div style={{ minWidth: "220px" }}>
              <div className="d-flex align-items-start mb-1">
                <i
                  className="fas fa-map-marker-alt me-2 text-danger mt-1"
                  style={{ fontSize: "0.85rem" }}
                ></i>
                <span
                  className="fw-semibold text-wrap"
                  style={{ fontSize: "0.82rem" }}
                >
                  {row.original.PickupAddress || "-"}
                </span>
              </div>
              {(row.original.SenderName || row.original.SenderMobile) && (
                <div className="d-flex align-items-center mt-1">
                  <i
                    className="fas fa-user me-2 text-secondary"
                    style={{ fontSize: "0.75rem" }}
                  ></i>
                  <small className="text-muted">
                    {row.original.SenderName || "-"}{" "}
                    {row.original.SenderMobile && (
                      <span className="text-dark fw-semibold">
                        · {row.original.SenderMobile}
                      </span>
                    )}
                  </small>
                </div>
              )}
              <div className="d-flex align-items-center mt-1">
                <i
                  className="fas fa-location-arrow me-2 text-info"
                  style={{ fontSize: "0.7rem" }}
                ></i>
                <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                  {row.original.PickupLatitude || "-"},{" "}
                  {row.original.PickupLongitude || "-"}
                </small>
              </div>

              {/* PICKUP PHOTO PREVIEW */}
              {pickupImg ? (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <div
                    role="button"
                    onClick={() => handleOpenPhotoModal(row.original, "pickup")}
                    title="Click to view full photo / replace"
                    style={{ cursor: "pointer", position: "relative" }}
                  >
                    <img
                      src={buildImageUrl(pickupImg)}
                      alt="Pickup Photo"
                      onError={e => {
                        e.currentTarget.onerror = null
                        e.currentTarget.style.display = "none"
                      }}
                      style={{
                        width: 54,
                        height: 54,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "2px solid #0d6efd",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                    <span
                      className="badge bg-primary position-absolute"
                      style={{
                        bottom: -3,
                        right: -3,
                        fontSize: "0.55rem",
                        padding: "1px 3px",
                      }}
                    >
                      <i className="fas fa-search-plus"></i>
                    </span>
                  </div>
                  <div>
                    <span
                      className="badge bg-primary bg-opacity-10 text-primary border border-primary fw-bold"
                      style={{ fontSize: "0.72rem" }}
                    >
                      📸 Pickup Photo
                    </span>
                    <div>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-primary fw-semibold"
                        style={{ fontSize: "0.7rem" }}
                        onClick={() =>
                          handleOpenPhotoModal(row.original, "pickup")
                        }
                      >
                        Inspect / Change
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary py-0.5 px-2 font-size-11 d-inline-flex align-items-center gap-1"
                    onClick={() => handleOpenPhotoModal(row.original, "pickup")}
                    title="Upload Pickup Photo"
                  >
                    <i className="fas fa-camera text-primary"></i>
                    <span>+ Add Pickup Photo</span>
                  </button>
                </div>
              )}
            </div>
          )
        },
      },
      {
        Header: "Stop 1 (Drop) & Photo",
        accessor: "Stop1Address",
        Cell: ({ row }) => {
          const dropImg = getDropPhoto(row.original)
          return (
            <div style={{ minWidth: "220px" }}>
              <div className="d-flex align-items-start mb-1">
                <i
                  className="fas fa-flag-checkered me-2 text-success mt-1"
                  style={{ fontSize: "0.85rem" }}
                ></i>
                <span
                  className="fw-semibold text-wrap"
                  style={{ fontSize: "0.82rem" }}
                >
                  {row.original.Stop1Address || "-"}
                </span>
              </div>
              {(row.original.Stop1ReceiverName ||
                row.original.Stop1ReceiverMobile) && (
                <div className="d-flex align-items-center mt-1">
                  <i
                    className="fas fa-user-check me-2 text-secondary"
                    style={{ fontSize: "0.75rem" }}
                  ></i>
                  <small className="text-muted">
                    {row.original.Stop1ReceiverName || "-"}{" "}
                    {row.original.Stop1ReceiverMobile && (
                      <span className="text-dark fw-semibold">
                        · {row.original.Stop1ReceiverMobile}
                      </span>
                    )}
                  </small>
                </div>
              )}
              <div className="d-flex align-items-center mt-1">
                <i
                  className="fas fa-location-arrow me-2 text-info"
                  style={{ fontSize: "0.7rem" }}
                ></i>
                <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                  {row.original.Stop1Latitude || "-"},{" "}
                  {row.original.Stop1Longitude || "-"}
                </small>
              </div>

              {/* DROP PHOTO PREVIEW */}
              {dropImg ? (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <div
                    role="button"
                    onClick={() => handleOpenPhotoModal(row.original, "drop")}
                    title="Click to view full photo / replace"
                    style={{ cursor: "pointer", position: "relative" }}
                  >
                    <img
                      src={buildImageUrl(dropImg)}
                      alt="Drop Delivery Photo"
                      onError={e => {
                        e.currentTarget.onerror = null
                        e.currentTarget.style.display = "none"
                      }}
                      style={{
                        width: 54,
                        height: 54,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "2px solid #198754",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                    <span
                      className="badge bg-success position-absolute"
                      style={{
                        bottom: -3,
                        right: -3,
                        fontSize: "0.55rem",
                        padding: "1px 3px",
                      }}
                    >
                      <i className="fas fa-search-plus"></i>
                    </span>
                  </div>
                  <div>
                    <span
                      className="badge bg-success bg-opacity-10 text-success border border-success fw-bold"
                      style={{ fontSize: "0.72rem" }}
                    >
                      🏁 Drop Photo
                    </span>
                    <div>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-success fw-semibold"
                        style={{ fontSize: "0.7rem" }}
                        onClick={() =>
                          handleOpenPhotoModal(row.original, "drop")
                        }
                      >
                        Inspect / Change
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary py-0.5 px-2 font-size-11 d-inline-flex align-items-center gap-1"
                    onClick={() => handleOpenPhotoModal(row.original, "drop")}
                    title="Upload Drop Delivery Photo"
                  >
                    <i className="fas fa-camera text-success"></i>
                    <span>+ Add Drop Photo</span>
                  </button>
                </div>
              )}
            </div>
          )
        },
      },
      {
        Header: "Trip KM",
        accessor: "TotalTripKM",
        Cell: ({ value }) => (
          <div className="text-center">
            <span
              className="badge bg-primary bg-opacity-10 text-primary fw-semibold"
              style={{ fontSize: "0.85rem" }}
            >
              <i className="fas fa-road me-1"></i>
              {value != null ? value : 0} km
            </span>
          </div>
        ),
      },
      {
        Header: "Goods & Fare",
        accessor: "GoodsType",
        Cell: ({ row }) => (
          <div className="text-center" style={{ minWidth: "115px" }}>
            <span className="badge bg-secondary bg-opacity-10 text-secondary fw-semibold d-block mb-1">
              {row.original.GoodsType || "General Goods"}
            </span>
            {row.original.Price != null && (
              <span className="badge bg-success fs-6 px-2 py-1 fw-bold d-inline-block">
                ₹ {row.original.FinalAmountCustomer || row.original.Price}
              </span>
            )}
            {row.original.AdvancePaidAmount != null &&
              Number(row.original.AdvancePaidAmount) > 0 &&
              Number(row.original.RemainingPaidAmount) > 0 && (
                <div className="mt-1">
                  <span
                    className="badge bg-warning text-dark font-size-10 px-1 py-0.5"
                    title={`Advance: ₹${row.original.AdvancePaidAmount} | Differential: ₹${row.original.RemainingPaidAmount}`}
                  >
                    <i className="fas fa-code-branch me-1"></i>Split Paid
                  </span>
                </div>
              )}
            {row.original.WaitingCharge != null &&
              Number(row.original.WaitingCharge) > 0 && (
                <small
                  className="text-warning d-block fw-bold"
                  style={{ fontSize: "0.72rem" }}
                >
                  +₹{row.original.WaitingCharge} wait
                </small>
              )}
            {row.original.Discount != null &&
              Number(row.original.Discount) > 0 && (
                <small
                  className="text-muted d-block"
                  style={{ fontSize: "0.7rem" }}
                >
                  Disc: ₹{row.original.Discount}
                </small>
              )}
          </div>
        ),
      },
      {
        Header: "Customer",
        accessor: "UserName",
        Cell: ({ row }) => (
          <div>
            <span className="fw-semibold d-block text-dark">
              {row.original.UserName || "-"}
            </span>
            <small className="text-muted font-monospace">
              {row.original.UserMobileNo || "-"}
            </small>
          </div>
        ),
      },
      {
        Header: "OTP",
        accessor: "OTP",
        Cell: ({ value }) => (
          <span
            className={`badge ${
              value ? "bg-info text-dark" : "bg-light text-muted"
            } fs-6 font-monospace`}
          >
            {value ? value.toString().padStart(4, "0") : "-"}
          </span>
        ),
      },
      {
        Header: "Customer Demand (Vehicle)",
        accessor: "VehicleTypeName",
        Cell: ({ row }) => {
          const vType =
            row.original.VehicleTypeName ||
            row.original.DemandedVehicleType ||
            "Not Specified"
          const vCategory =
            row.original.VehicleCategory ||
            row.original.VehicleCategoryName ||
            ""
          const vCapacity = row.original.DemandedWeightCapacity
            ? `${row.original.DemandedWeightCapacity} kg`
            : row.original.WeightRangeTo
            ? `Max ${row.original.WeightRangeTo} kg`
            : ""

          return (
            <div style={{ minWidth: "170px" }}>
              <div className="d-flex align-items-center mb-1">
                <span
                  className="badge bg-primary bg-opacity-10 text-primary border border-primary fw-bold"
                  style={{ fontSize: "0.82rem" }}
                >
                  <i className="fas fa-truck-loading me-1"></i>
                  {vType}
                </span>
              </div>
              <div className="small text-muted">
                {vCategory && (
                  <span className="fw-semibold text-dark">{vCategory}</span>
                )}
                {vCategory && vCapacity && <span> · </span>}
                {vCapacity && (
                  <span className="text-secondary">{vCapacity}</span>
                )}
              </div>
              {row.original.GoodsType && (
                <div
                  className="small text-muted mt-1"
                  style={{ fontSize: "0.72rem" }}
                >
                  <i className="fas fa-box me-1"></i>
                  {row.original.GoodsType}
                </div>
              )}
            </div>
          )
        },
      },
      {
        Header: "Assigned Driver & Vehicle",
        accessor: "DriverName",
        Cell: ({ row }) => {
          const hasDriver = Boolean(
            row.original.DriverName ||
              row.original.VehicleNo ||
              (row.original.F_VehicleMaster &&
                Number(row.original.F_VehicleMaster) > 0)
          )
          const vehicleNo =
            row.original.VehicleNo ||
            row.original.VehicleRegistrationNo ||
            row.original.VehicleName

          if (!hasDriver) {
            return (
              <div style={{ minWidth: "160px" }}>
                <span className="badge bg-warning bg-opacity-25 text-dark border border-warning px-2 py-1 mb-1 d-inline-block">
                  <i className="fas fa-exclamation-circle me-1 text-warning"></i>
                  Pending Driver
                </span>
                <div>
                  <Button
                    size="sm"
                    className="btn btn-sm btn-primary py-0.5 px-2 font-size-11"
                    onClick={() => btnAssignDriverOnClick(row.original)}
                  >
                    <i className="bx bx-user-plus me-1 font-size-12"></i>
                    Assign Driver
                  </Button>
                </div>
              </div>
            )
          }

          return (
            <div style={{ minWidth: "180px" }}>
              {vehicleNo && (
                <div className="mb-1">
                  <span
                    className="badge bg-dark text-warning border border-secondary px-2 py-1 font-monospace fw-bold"
                    style={{ letterSpacing: "0.5px" }}
                  >
                    <i className="fas fa-car me-1 text-light"></i>
                    {vehicleNo}
                  </span>
                </div>
              )}
              <div
                className="fw-semibold text-dark"
                style={{ fontSize: "0.85rem" }}
              >
                <i className="fas fa-user-check me-1 text-success"></i>
                {row.original.DriverName || "-"}
              </div>
              {row.original.DriverMobileNo && (
                <div className="text-muted small">
                  <i className="fas fa-phone-alt me-1 font-size-10 text-primary"></i>
                  {row.original.DriverMobileNo}
                </div>
              )}
              {row.original.DriverVehicleTypeName && (
                <div
                  className="text-muted small"
                  style={{ fontSize: "0.72rem" }}
                >
                  {row.original.DriverVehicleTypeName}
                </div>
              )}
            </div>
          )
        },
      },
      {
        Header: "Status",
        accessor: "Status",
        Cell: ({ row }) => {
          const statusId = Number(row.original?.F_StatusMaster || 0)
          const statusName = row.original?.Status || ""
          let badgeClass = "bg-secondary text-white"
          let iconClass = "fas fa-info-circle"
          let displayLabel = statusName || "Unknown"

          if (statusId === 1) {
            badgeClass = "bg-warning text-dark border border-warning"
            iconClass = "fas fa-clock"
            displayLabel = "Pending Assignment"
          } else if (statusId === 2) {
            badgeClass = "bg-primary text-white"
            iconClass = "fas fa-user-check"
            displayLabel = "Driver Assigned"
          } else if (statusId === 3) {
            badgeClass = "bg-info text-dark"
            iconClass = "fas fa-car-side"
            displayLabel = "Heading to Pickup"
          } else if (statusId === 4) {
            badgeClass = "bg-info text-dark"
            iconClass = "fas fa-map-marker-alt"
            displayLabel = "Reached Pickup"
          } else if (statusId >= 5 && statusId <= 17) {
            badgeClass = "bg-primary text-white"
            iconClass = "fas fa-route"
            displayLabel = statusName || "In Transit"
          } else if (statusId === 18) {
            badgeClass = "bg-success text-white"
            iconClass = "fas fa-check-circle"
            displayLabel = "Completed"
          } else if (statusId === 19) {
            badgeClass = "bg-danger text-white"
            iconClass = "fas fa-times-circle"
            displayLabel = "Cancelled"
          }

          return (
            <span
              className={`badge ${badgeClass} d-inline-flex align-items-center gap-1 px-2 py-1.5`}
              style={{ fontSize: "0.8rem", borderRadius: "6px" }}
            >
              <i className={iconClass} style={{ fontSize: "0.75rem" }}></i>
              <span>{displayLabel}</span>
            </span>
          )
        },
      },
      {
        Header: "Admin Actions",
        accessor: "Actions",
        Cell: ({ row }) => {
          const booking = row.original
          const statusId = Number(booking.F_StatusMaster || 0)
          const hasDriver = Boolean(
            booking.DriverName ||
              booking.VehicleNo ||
              (booking.F_VehicleMaster && Number(booking.F_VehicleMaster) > 0)
          )
          const isCancellable = statusId >= 1 && statusId <= 17

          return (
            <div
              className="d-flex flex-column gap-1.5 align-items-stretch"
              style={{ minWidth: "155px" }}
            >
              {/* 1. Live Track */}
              <Button
                variant="info"
                size="sm"
                className="text-white d-flex align-items-center justify-content-center gap-1 py-1 px-2 font-size-12 fw-semibold"
                onClick={() => {
                  setLiveTrackBooking(booking)
                  setShowLiveTrackModal(true)
                }}
                title="Live Track Trip in Real-Time via SignalR"
              >
                <i className="fas fa-satellite-dish font-size-11"></i>
                <span>Live Track</span>
              </Button>

              {/* 2. Assign / Reassign Driver (ADMIN UNRESTRICTED RIGHT) */}
              <Button
                variant={hasDriver ? "outline-primary" : "primary"}
                size="sm"
                className="d-flex align-items-center justify-content-center gap-1 py-1 px-2 font-size-12 fw-semibold"
                onClick={() => btnAssignDriverOnClick(booking)}
                title={
                  hasDriver
                    ? "Reassign or Change Assigned Driver/Vehicle"
                    : "Assign Driver to this Booking"
                }
              >
                <i
                  className={hasDriver ? "fas fa-user-edit" : "bx bx-user-plus"}
                ></i>
                <span>{hasDriver ? "Reassign Driver" : "Assign Driver"}</span>
              </Button>

              <div className="d-flex gap-1">
                {/* 3. Edit Booking (ADMIN UNRESTRICTED RIGHT: Price, Route, Cargo, Vehicle) */}
                <Button
                  variant="warning"
                  size="sm"
                  className="flex-fill text-dark d-flex align-items-center justify-content-center gap-1 py-1 px-2 font-size-11 fw-semibold"
                  onClick={() => handleOpenEditModal(booking)}
                  title="Edit Booking Details (Price, Route, Customer, Goods)"
                >
                  <i className="bx bx-edit"></i>
                  <span>Edit</span>
                </Button>

                {/* 4. Quick Status Changer (ADMIN RIGHT) */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="flex-fill d-flex align-items-center justify-content-center gap-1 py-1 px-2 font-size-11 fw-semibold"
                  onClick={() => handleOpenStatusModal(booking)}
                  title="Change Booking Status"
                >
                  <i className="fas fa-tasks"></i>
                  <span>Status</span>
                </Button>
              </div>

              <div className="d-flex gap-1">
                {/* 5. View/Upload Photos */}
                <Button
                  variant="outline-dark"
                  size="sm"
                  className="flex-fill d-flex align-items-center justify-content-center gap-1 py-0.5 px-1 font-size-11"
                  onClick={() => handleOpenPhotoModal(booking)}
                  title="View or Upload Delivery & Pickup Photos"
                >
                  <i className="fas fa-images text-primary"></i>
                  <span>Photos</span>
                </Button>

                {/* 6. Cancel Booking */}
                {isCancellable && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="flex-fill d-flex align-items-center justify-content-center gap-1 py-0.5 px-1 font-size-11"
                    onClick={() => btnCancelOnClick(booking)}
                    title="Cancel Booking"
                  >
                    <i className="fas fa-ban"></i>
                    <span>Cancel</span>
                  </Button>
                )}
              </div>
            </div>
          )
        },
      },
    ],
    [btnAssignDriverOnClick, btnCancelOnClick]
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [
          {
            id: "Id",
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  if (loading) {
    return (
      <div
        className="master-list-loading d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-muted">Loading bookings...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="master-list-container">
      <Container fluid={true} className="px-3 px-md-4">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />

        {/* Controls Section */}
        <div className="master-list-controls master-list-slide-in mb-4">
          <Row className="align-items-center">
            <Col md={6} sm={12} className="mb-3 mb-md-0">
              <div className="search-wrapper">
                <FormControl
                  type="text"
                  placeholder="Search booking by pickup, drop, customer, driver..."
                  value={globalFilter || ""}
                  onChange={e => setGlobalFilter(e.target.value)}
                  className="form-control-lg"
                />
              </div>
            </Col>
            <Col md={6} sm={12}>
              <div className="d-flex align-items-center gap-3 justify-content-md-end flex-wrap">
                <ExportButtons
                  title="Booking Report"
                  columns={columns}
                  data={data}
                  filename="Booking_Report"
                />
                <span
                  className="badge bg-success-subtle text-success border border-success d-inline-flex align-items-center gap-1 px-2 py-2"
                  title={
                    isConnected ? "Real-time SignalR active" : "Connecting..."
                  }
                >
                  <span
                    className={`spinner-grow spinner-grow-sm ${
                      isConnected ? "text-success" : "text-warning"
                    }`}
                    role="status"
                    style={{ width: 8, height: 8 }}
                  />
                  <span>
                    {isConnected ? "Live Auto-Refresh" : "Connecting..."}
                  </span>
                </span>
                <span className="fw-semibold text-muted">Status:</span>
                <select
                  className="form-select form-select-lg"
                  style={{ minWidth: "220px", fontWeight: "500" }}
                  value={selectedStatusId}
                  onChange={handleStatusFilterChange}
                >
                  {STATUS_FILTER_OPTIONS.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
            </Col>
          </Row>
        </div>

        {/* Live New Booking Notification Banner */}
        {newlyAddedId && (
          <div
            className="alert alert-success alert-dismissible fade show d-flex align-items-center justify-content-between mb-3 py-2 px-3 shadow-sm border border-success"
            role="alert"
          >
            <div className="d-flex align-items-center">
              <i className="bx bx-bell bx-tada font-size-18 me-2 text-success" />
              <div>
                <strong>⚡ New Ride Booked (#{newlyAddedId})!</strong> Table has
                been automatically updated in real time.
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setNewlyAddedId(null)}
            />
          </div>
        )}

        {/* Table Section */}
        <div className="master-list-table-container master-list-bounce-in mb-4">
          <div className="table-responsive">
            <Table
              {...getTableProps()}
              className="master-list-table table-hover"
            >
              <thead style={{ backgroundColor: "#E87B2D", color: "#fff" }}>
                {headerGroups.map((headerGroup, headerGroupIndex) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={headerGroupIndex}
                  >
                    {headerGroup.headers.map((column, columnIndex) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        key={columnIndex}
                        className="align-middle"
                        style={{
                          padding: "8px 6px",
                          fontSize: 12,
                          fontWeight: 600,
                          backgroundColor: "#E87B2D",
                          color: "#fff",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="fw-semibold">
                            {column.render("Header")}
                          </span>
                          <span
                            className="sort-indicator"
                            style={{ color: "#fff" }}
                          >
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <i
                                  className="fas fa-sort-down"
                                  style={{ fontSize: "0.7rem" }}
                                ></i>
                              ) : (
                                <i
                                  className="fas fa-sort-up"
                                  style={{ fontSize: "0.7rem" }}
                                ></i>
                              )
                            ) : (
                              <i
                                className="fas fa-sort"
                                style={{ opacity: 0.7, fontSize: "0.7rem" }}
                              ></i>
                            )}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, rowIndex) => {
                  prepareRow(row)
                  const isHighlighted =
                    newlyAddedId &&
                    String(row.original?.Id) === String(newlyAddedId)
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={rowIndex}
                      className={`align-middle ${
                        isHighlighted
                          ? "table-warning border-2 border-warning"
                          : ""
                      }`}
                      style={
                        isHighlighted
                          ? {
                              backgroundColor: "#fff3cd",
                              transition: "background 0.5s ease",
                            }
                          : {}
                      }
                    >
                      {row.cells.map((cell, cellIndex) => (
                        <td
                          {...cell.getCellProps()}
                          key={cellIndex}
                          className="py-2.5"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination Section */}
        <div className="master-list-pagination">
          <Row className="align-items-center">
            <Col md={6} sm={12} className="mb-3 mb-md-0">
              <div className="master-list-pagination-info d-flex flex-wrap align-items-center gap-3">
                <span className="text-muted">
                  Page <strong>{pageIndex + 1}</strong> of{" "}
                  <strong>{pageOptions.length}</strong> ({data.length} total
                  bookings)
                </span>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Go to:</span>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: "80px" }}
                    defaultValue={pageIndex + 1}
                    onChange={e => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0
                      gotoPage(page)
                    }}
                  />
                </div>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "120px" }}
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 30, 50, 100].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </Col>
            <Col md={6} sm={12}>
              <div className="master-list-pagination-buttons d-flex justify-content-md-end gap-2">
                <Button
                  className="pagination-btn"
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-double-left"></i>
                </Button>
                <Button
                  className="pagination-btn"
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-left"></i>
                </Button>
                <Button
                  className="pagination-btn"
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-right"></i>
                </Button>
                <Button
                  className="pagination-btn"
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-double-right"></i>
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      {/* ============================================================== */}
      {/* 1. ASSIGN / REASSIGN DRIVER MODAL                                */}
      {/* ============================================================== */}
      <Modal
        show={showAssignModal}
        onHide={handleAssignModalClose}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-bottom pb-3">
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                backgroundColor: "#e8f2fc",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0d6efd",
                fontSize: "1.2rem",
              }}
            >
              <i className="bx bx-user-check"></i>
            </span>
            <div>
              <Modal.Title className="h5 mb-0 fw-bold">
                {selectedBooking?.F_VehicleMaster &&
                Number(selectedBooking.F_VehicleMaster) > 0
                  ? "Reassign / Change Driver & Vehicle"
                  : "Assign Driver & Vehicle"}
              </Modal.Title>
              {selectedBooking && (
                <small className="text-muted">
                  Booking #{selectedBooking.Id} · Current Status:{" "}
                  {selectedBooking.Status || "Pending"}
                </small>
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {selectedBooking ? (
            <>
              {/* If already assigned, show current assignment alert */}
              {selectedBooking.F_VehicleMaster &&
                Number(selectedBooking.F_VehicleMaster) > 0 && (
                  <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-between small">
                    <div>
                      <i className="fas fa-info-circle me-1"></i>
                      <strong>Current Driver:</strong>{" "}
                      {selectedBooking.DriverName || "Assigned"} (
                      {selectedBooking.VehicleNo ||
                        selectedBooking.VehicleName ||
                        "Vehicle"}
                      )
                    </div>
                    <span className="badge bg-primary">
                      Admin Reassignment Mode
                    </span>
                  </div>
                )}

              {/* 1. CUSTOMER DEMANDED VEHICLE BANNER */}
              <div
                className="p-3 rounded mb-3"
                style={{
                  backgroundColor: "#eff6ff",
                  border: "1.5px solid #bfdbfe",
                }}
              >
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span
                        className="badge bg-primary px-2.5 py-1 fw-bold text-uppercase"
                        style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
                      >
                        <i className="fas fa-truck-loading me-1"></i> Customer
                        Demanded Vehicle
                      </span>
                      {selectedBooking.VehicleCategory && (
                        <span
                          className="badge bg-secondary bg-opacity-25 text-dark fw-semibold"
                          style={{ fontSize: "0.75rem" }}
                        >
                          Category: {selectedBooking.VehicleCategory}
                        </span>
                      )}
                    </div>
                    <h5
                      className="fw-bold text-primary mb-1"
                      style={{ fontSize: "1.2rem" }}
                    >
                      🛺{" "}
                      {selectedBooking.VehicleTypeName ||
                        selectedBooking.DemandedVehicleType ||
                        "Vehicle Type Not Specified"}
                    </h5>
                    <div className="text-muted small">
                      <span>Max Required Capacity: </span>
                      <strong className="text-dark">
                        {selectedBooking.DemandedWeightCapacity
                          ? `${selectedBooking.DemandedWeightCapacity} kg`
                          : selectedBooking.WeightRangeTo
                          ? `Up to ${selectedBooking.WeightRangeTo} kg`
                          : "-"}
                      </strong>
                    </div>
                  </div>

                  <div className="text-end">
                    <div className="badge bg-success fs-6 px-3 py-1.5 fw-bold mb-1 d-inline-block">
                      ₹{" "}
                      {selectedBooking.Price != null
                        ? selectedBooking.Price
                        : "0"}
                    </div>
                    <div className="text-muted small fw-semibold">
                      <i className="fas fa-road me-1 text-primary"></i>
                      {selectedBooking.TotalTripKM || 0} km Trip
                    </div>
                  </div>
                </div>

                {/* Goods / Item Details */}
                {(selectedBooking.GoodsType ||
                  selectedBooking.ItemDetails ||
                  selectedBooking.ItemWeight) && (
                  <div className="mt-2 pt-2 border-top border-primary border-opacity-25 d-flex flex-wrap gap-3 small text-dark">
                    <div>
                      <span className="text-muted">📦 Goods Type: </span>
                      <strong>
                        {selectedBooking.GoodsType || "General Goods"}
                      </strong>
                    </div>
                    {selectedBooking.ItemWeight && (
                      <div>
                        <span className="text-muted">⚖️ Item Weight: </span>
                        <strong>{selectedBooking.ItemWeight} kg</strong>
                      </div>
                    )}
                    {selectedBooking.ItemDetails && (
                      <div>
                        <span className="text-muted">📝 Details: </span>
                        <strong>{selectedBooking.ItemDetails}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 2. PICKUP & DROP ROUTE DETAILS */}
              <Row className="g-2 mb-3">
                <Col md={6}>
                  <div className="border rounded p-2.5 h-100 bg-white shadow-sm">
                    <div className="d-flex align-items-center text-primary mb-1">
                      <i className="fas fa-map-marker-alt me-1.5"></i>
                      <span className="text-uppercase small fw-bold">
                        Pickup Location
                      </span>
                    </div>
                    <div
                      className="fw-semibold text-dark small mb-1"
                      style={{ lineHeight: "1.3" }}
                    >
                      {selectedBooking.PickupAddress || "-"}
                    </div>
                    <div className="text-muted small">
                      <i className="fas fa-user me-1 text-secondary"></i>
                      {selectedBooking.SenderName ||
                        selectedBooking.UserName ||
                        "-"}
                      {(selectedBooking.SenderMobile ||
                        selectedBooking.UserMobileNo) && (
                        <span className="fw-semibold text-dark">
                          {" "}
                          ·{" "}
                          {selectedBooking.SenderMobile ||
                            selectedBooking.UserMobileNo}
                        </span>
                      )}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="border rounded p-2.5 h-100 bg-white shadow-sm">
                    <div className="d-flex align-items-center text-success mb-1">
                      <i className="fas fa-flag-checkered me-1.5"></i>
                      <span className="text-uppercase small fw-bold">
                        Drop / Stop 1
                      </span>
                    </div>
                    <div
                      className="fw-semibold text-dark small mb-1"
                      style={{ lineHeight: "1.3" }}
                    >
                      {selectedBooking.Stop1Address ||
                        selectedBooking.Stop1ReceiverHouseDetails ||
                        selectedBooking.DropAddress ||
                        "-"}
                    </div>
                    <div className="text-muted small">
                      <i className="fas fa-user-check me-1 text-secondary"></i>
                      {selectedBooking.Stop1ReceiverName || "-"}
                      {selectedBooking.Stop1ReceiverMobile && (
                        <span className="fw-semibold text-dark">
                          {" "}
                          · {selectedBooking.Stop1ReceiverMobile}
                        </span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* 3. DYNAMIC REASSIGNMENT WARNING / NOTIFICATION BANNER */}
              {selectedBooking.F_VehicleMaster &&
                Number(selectedBooking.F_VehicleMaster) > 0 &&
                selectedDriverId &&
                String(selectedDriverId) !==
                  String(selectedBooking.F_VehicleMaster) && (
                  <div
                    className="alert alert-warning py-2.5 px-3 mb-3 border-warning d-flex align-items-center gap-2 shadow-sm"
                    style={{
                      backgroundColor: "#fffbeb",
                      borderColor: "#fde68a",
                    }}
                  >
                    <i className="fas fa-exchange-alt fs-4 text-warning"></i>
                    <div className="flex-grow-1">
                      <div className="fw-bold text-dark">
                        🔄 Driver Reassignment in Progress
                      </div>
                      <div className="small text-muted">
                        Changing driver from:{" "}
                        <span className="badge bg-secondary text-white">
                          {selectedBooking.DriverName || "Current Driver"} (
                          {selectedBooking.VehicleNo ||
                            selectedBooking.VehicleName ||
                            "Vehicle"}
                          )
                        </span>{" "}
                        ➔ to:{" "}
                        <span className="badge bg-primary text-white">
                          {selectedDriverDetails?.DriverName ||
                            "Selected Driver"}{" "}
                          (
                          {selectedDriverDetails?.VehicleNo ||
                            selectedDriverDetails?.VehicleRegistrationNo ||
                            "Vehicle"}
                          )
                        </span>
                      </div>
                    </div>
                    <span className="badge bg-warning text-dark fw-bold px-2 py-1">
                      Pending Confirmation
                    </span>
                  </div>
                )}

              {/* 4. DRIVER & VEHICLE SELECTION (REACT-SELECT SEARCHABLE DROPDOWN) */}
              <div className="border rounded p-3 bg-light mb-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <Form.Label className="fw-bold mb-0 text-dark">
                    <i className="fas fa-id-card me-1 text-primary"></i>
                    Select Driver & Vehicle (Searchable Dropdown)
                  </Form.Label>
                  <small className="text-muted">
                    Total <strong>{driverOptions.length}</strong> registered
                    drivers available
                  </small>
                </div>

                {/* React-Select Searchable Dropdown */}
                <Select
                  value={selectedDriverOption}
                  onChange={opt => setSelectedDriverId(opt ? opt.value : "")}
                  options={reactSelectDriverOptions}
                  styles={reactSelectCustomStyles}
                  placeholder="🔍 Type driver name, vehicle plate (e.g. RJ19), mobile number..."
                  isSearchable={true}
                  isClearable={false}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  formatOptionLabel={option => {
                    if (option.isDefault) {
                      return (
                        <span className="text-muted fw-semibold">
                          -- Select Driver & Vehicle --
                        </span>
                      )
                    }
                    return (
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 w-100">
                        <div className="d-flex align-items-center gap-1.5">
                          {option.isCurrentAssigned && (
                            <span
                              className="badge bg-primary text-white"
                              style={{ fontSize: "0.7rem", padding: "3px 6px" }}
                            >
                              <i className="fas fa-lock me-1"></i> CURRENT
                              DRIVER
                            </span>
                          )}
                          {option.isMatch && (
                            <span
                              className="badge bg-success text-white"
                              style={{ fontSize: "0.7rem", padding: "3px 6px" }}
                            >
                              <i className="fas fa-star me-1"></i> RECOMMENDED
                              MATCH
                            </span>
                          )}
                          <span
                            className="badge font-monospace text-dark px-1.5 py-0.5"
                            style={{
                              backgroundColor: "#fef08a",
                              border: "1px solid #ca8a04",
                              fontSize: "0.82rem",
                            }}
                          >
                            {option.plate}
                          </span>
                          <span className="fw-bold text-dark">
                            {option.driverName}
                          </span>
                          {option.mobile && option.mobile !== "-" && (
                            <span className="text-muted small">
                              · 📞 {option.mobile}
                            </span>
                          )}
                        </div>
                        <div className="small text-muted">
                          <span className="fw-semibold text-primary">
                            {option.vehicleType}
                          </span>{" "}
                          ({option.capacity} kg)
                        </div>
                      </div>
                    )
                  }}
                />

                <div className="d-flex justify-content-between align-items-center mt-2 px-1">
                  <small className="text-muted">
                    💡{" "}
                    <em>
                      You can type vehicle plate, driver name or mobile to
                      filter instantly.
                    </em>
                  </small>
                  {matchingDriversCount > 0 && (
                    <small className="text-success fw-semibold">
                      ⭐ {matchingDriversCount} matching vehicles prioritized at
                      top
                    </small>
                  )}
                </div>
              </div>

              {/* 5. SELECTED DRIVER & VEHICLE DETAILED PREVIEW CARD */}
              {selectedDriverDetails && (
                <div
                  className="border rounded p-3 mb-2"
                  style={{
                    backgroundColor: "#ffffff",
                    borderLeft: "4px solid #0d6efd",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <span className="fw-bold text-dark font-size-13 text-uppercase">
                      <i className="fas fa-clipboard-check text-success me-1"></i>
                      Assigned Driver & Vehicle Preview
                    </span>

                    {/* VEHICLE LICENSE PLATE BADGE */}
                    <span
                      style={{
                        backgroundColor: "#fef08a",
                        color: "#000",
                        border: "2px solid #ca8a04",
                        borderRadius: "4px",
                        padding: "3px 10px",
                        fontFamily: "monospace",
                        fontWeight: "800",
                        fontSize: "0.95rem",
                        letterSpacing: "1px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      }}
                    >
                      🇮🇳{" "}
                      {selectedDriverDetails.VehicleNo?.trim() ||
                        selectedDriverDetails.VehicleRegistrationNo?.trim() ||
                        "NO PLATE"}
                    </span>
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small d-block">
                          Driver Details
                        </span>
                        <div className="fw-bold text-dark fs-6 mt-0.5">
                          {selectedDriverDetails.DriverName || "-"}
                        </div>
                        {selectedDriverDetails.DriverMobileNo && (
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <a
                              href={`tel:${selectedDriverDetails.DriverMobileNo}`}
                              className="btn btn-sm btn-outline-primary py-0 px-2 font-size-11"
                              title="Call Driver"
                            >
                              <i className="fas fa-phone-alt me-1"></i>
                              {selectedDriverDetails.DriverMobileNo}
                            </a>
                            <a
                              href={`https://wa.me/91${selectedDriverDetails.DriverMobileNo.replace(
                                /[^0-9]/g,
                                ""
                              ).slice(-10)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-success py-0 px-2 font-size-11"
                              title="WhatsApp Driver"
                            >
                              <i className="fab fa-whatsapp me-1"></i>
                              Chat
                            </a>
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small d-block">
                          Vehicle Details
                        </span>
                        <div className="fw-bold text-dark fs-6 mt-0.5">
                          {selectedDriverDetails.VehicleTypeName || "-"}
                        </div>
                        <div className="text-muted small mt-1">
                          Capacity:{" "}
                          <strong className="text-dark">
                            {selectedDriverDetails.WeightCapacity || "-"} kg
                          </strong>
                        </div>
                        {selectedDriverDetails.VehicleRegistrationNo && (
                          <div
                            className="text-muted small font-monospace"
                            style={{ fontSize: "0.72rem" }}
                          >
                            RC / Reg:{" "}
                            {selectedDriverDetails.VehicleRegistrationNo}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Demand Match Check Banner */}
                  {selectedBooking && (
                    <div className="mt-3">
                      {Number(selectedDriverDetails.F_VehicleType) ===
                      Number(selectedBooking.F_VehicleType) ? (
                        <div className="alert alert-success py-2 px-3 mb-0 d-flex align-items-center gap-2 small fw-semibold">
                          <i className="fas fa-check-circle fs-5 text-success"></i>
                          <span>
                            ✓ Perfect Match: Selected vehicle (
                            {selectedDriverDetails.VehicleTypeName}) matches
                            customer requirement!
                          </span>
                        </div>
                      ) : (
                        <div className="alert alert-warning py-2 px-3 mb-0 d-flex align-items-center gap-2 small fw-semibold">
                          <i className="fas fa-exclamation-triangle fs-5 text-warning"></i>
                          <span>
                            ⚠️ Vehicle Mismatch Notice: Selected driver vehicle
                            (
                            {selectedDriverDetails.VehicleTypeName ||
                              "Different Type"}
                            ) does not match demanded vehicle (
                            {selectedBooking.VehicleTypeName ||
                              selectedBooking.VehicleCategory}
                            ). Please verify before confirming!
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-4">
              No booking selected.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={handleAssignModalClose}
            disabled={isAssigningDriver}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignConfirm}
            disabled={
              !selectedBooking || !selectedDriverId || isAssigningDriver
            }
          >
            {isAssigningDriver ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Updating Assignment...
              </>
            ) : selectedBooking?.F_VehicleMaster &&
              Number(selectedBooking.F_VehicleMaster) > 0 ? (
              selectedDriverId &&
              String(selectedDriverId) !==
                String(selectedBooking.F_VehicleMaster) ? (
                `Confirm Reassignment to ${
                  selectedDriverDetails?.DriverName || "New Driver"
                }`
              ) : (
                "Re-confirm Assignment"
              )
            ) : (
              `Confirm Assignment to ${
                selectedDriverDetails?.DriverName || "Driver"
              }`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============================================================== */}
      {/* 2. ADMIN EDIT BOOKING MODAL (FULL CONTROL)                       */}
      {/* ============================================================== */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
        backdrop="static"
      >
        <Form onSubmit={handleSaveEditBooking}>
          <Modal.Header closeButton className="border-bottom pb-3">
            <div className="d-flex align-items-center gap-2">
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  backgroundColor: "#fff3cd",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#d97706",
                  fontSize: "1.2rem",
                }}
              >
                <i className="bx bx-edit"></i>
              </span>
              <div>
                <Modal.Title className="h5 mb-0 fw-bold">
                  Edit Booking #{editFormData?.Id} (Admin Full Control)
                </Modal.Title>
                <small className="text-muted">
                  Modify fare, demanded vehicle, routes, or contact details.
                </small>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="pt-3">
            <style>{`
              .pac-container {
                z-index: 99999 !important;
                border-radius: 8px;
                box-shadow: 0 4px 16px rgba(0,0,0,0.2);
              }
            `}</style>
            {editFormData && (
              <>
                {/* SECTION 1: FARE & DISTANCE */}
                <div className="p-3 rounded mb-3 bg-light border">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="fw-bold text-dark mb-0">
                      <i className="fas fa-rupee-sign me-1 text-success"></i>{" "}
                      Fare & Trip Distance
                    </h6>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      type="button"
                      disabled={isRecalculatingRate}
                      onClick={() =>
                        triggerRouteDistanceAndFareCheck(
                          editFormData.PickupLatitude,
                          editFormData.PickupLongitude,
                          editFormData.Stop1Latitude,
                          editFormData.Stop1Longitude
                        )
                      }
                      className="py-1 px-2"
                      title="Calculate road distance from pickup/drop and check fare"
                    >
                      {isRecalculatingRate ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                          Calculating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sync-alt me-1"></i> Recalculate
                          Distance & Fare
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Status Notice according to rule: Distance <= Orig -> No fare change; Distance > Orig -> Fare recalculated */}
                  {fareRecalcStatus && (
                    <div
                      className={`alert alert-${
                        fareRecalcStatus.type === "success"
                          ? "success"
                          : fareRecalcStatus.type === "info"
                          ? "info"
                          : fareRecalcStatus.type === "calculating"
                          ? "warning"
                          : "secondary"
                      } py-2 px-3 mb-2 small d-flex align-items-center gap-2`}
                    >
                      {fareRecalcStatus.type === "calculating" && (
                        <Spinner animation="border" size="sm" />
                      )}
                      {fareRecalcStatus.type === "success" && (
                        <i className="fas fa-check-circle text-success fs-6"></i>
                      )}
                      {fareRecalcStatus.type === "info" && (
                        <i className="fas fa-info-circle text-info fs-6"></i>
                      )}
                      {fareRecalcStatus.type === "warning" && (
                        <i className="fas fa-exclamation-triangle text-warning fs-6"></i>
                      )}
                      <div className="flex-grow-1">
                        <strong>{fareRecalcStatus.message}</strong>
                      </div>
                    </div>
                  )}

                  <Row className="g-2">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Price / Total Fare (₹)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={editFormData.Price}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              Price: e.target.value,
                            })
                          }
                          required
                          className="fw-bold text-success fs-6"
                        />
                        {originalBookingState && (
                          <small
                            className="text-muted d-block"
                            style={{ fontSize: "11px" }}
                          >
                            Original Fare: ₹{originalBookingState.Price}
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Discount (₹)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={editFormData.Discount}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              Discount: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Trip Distance (KM)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={editFormData.TotalTripKM}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              TotalTripKM: e.target.value,
                            })
                          }
                        />
                        {originalBookingState && (
                          <small
                            className="text-muted d-block"
                            style={{ fontSize: "11px" }}
                          >
                            Original Distance:{" "}
                            {originalBookingState.TotalTripKM} KM
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* SECTION 2: VEHICLE & CARGO DETAILS */}
                <div className="p-3 rounded mb-3 bg-light border">
                  <h6 className="fw-bold text-dark mb-2">
                    <i className="fas fa-truck-loading me-1 text-primary"></i>{" "}
                    Demanded Vehicle & Cargo
                  </h6>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Demanded Vehicle Type
                        </Form.Label>
                        <Form.Select
                          value={editFormData.F_VehicleType}
                          onChange={e => {
                            const newVType = e.target.value
                            setEditFormData({
                              ...editFormData,
                              F_VehicleType: newVType,
                            })
                            if (
                              originalBookingState &&
                              Number(editFormData.TotalTripKM) >
                                Number(originalBookingState.TotalTripKM)
                            ) {
                              triggerRouteDistanceAndFareCheck(
                                editFormData.PickupLatitude,
                                editFormData.PickupLongitude,
                                editFormData.Stop1Latitude,
                                editFormData.Stop1Longitude,
                                newVType
                              )
                            }
                          }}
                        >
                          <option value="">
                            -- Select Demanded Vehicle Type --
                          </option>
                          {vehicleTypesList.map(vt => (
                            <option key={vt.Id} value={String(vt.Id)}>
                              {vt.Name} (Capacity: {vt.WeightCapacity} kg)
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Goods Type
                        </Form.Label>
                        <Form.Control
                          type="text"
                          list="goodsTypesDataList"
                          value={editFormData.GoodsType}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              GoodsType: e.target.value,
                            })
                          }
                          placeholder="e.g. Electronics & Appliances, Furniture, Documents..."
                        />
                        <datalist id="goodsTypesDataList">
                          {goodsTypesList.map(gt => (
                            <option key={gt.Id} value={gt.Name}>
                              {gt.Icon ? `${gt.Icon} ${gt.Name}` : gt.Name}
                            </option>
                          ))}
                        </datalist>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Item Details / Description
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={editFormData.ItemDetails}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              ItemDetails: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Item Weight (kg)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={editFormData.ItemWeight}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              ItemWeight: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* SECTION 3: PICKUP DETAILS */}
                <div className="p-3 rounded mb-3 bg-light border shadow-sm">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="fw-bold text-dark mb-0">
                      <i className="fas fa-map-marker-alt me-1 text-danger"></i>{" "}
                      Pickup Location & Contact
                    </h6>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        type="button"
                        className="py-1 px-2"
                        onClick={() => handleDetectCurrentLocation("pickup")}
                        title="Detect device GPS"
                      >
                        <i className="fas fa-crosshairs me-1"></i> My GPS
                      </Button>
                      {editFormData.PickupLatitude &&
                        editFormData.PickupLongitude && (
                          <a
                            href={`https://www.google.com/maps?q=${editFormData.PickupLatitude},${editFormData.PickupLongitude}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-secondary py-1 px-2"
                            title="Open in Google Maps"
                          >
                            <i className="fas fa-external-link-alt me-1"></i>{" "}
                            View Map
                          </a>
                        )}
                    </div>
                  </div>

                  {/* Search with Google Places Autocomplete */}
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-muted mb-1">
                      <i className="fab fa-google text-primary me-1"></i> Search
                      Pickup Location (Google Places Autocomplete)
                    </Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fas fa-search text-muted"></i>
                      </span>
                      <input
                        ref={pickupSearchInputRef}
                        type="text"
                        className="form-control"
                        placeholder="Type location to auto-search & populate coordinates..."
                      />
                    </div>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      Selecting a place automatically updates the address and
                      exact GPS coordinates below.
                    </small>
                  </Form.Group>

                  {/* Direct Latitude & Longitude Inputs */}
                  <Row className="g-2 mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          <i className="fas fa-compass me-1 text-danger"></i>{" "}
                          Pickup Latitude
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="any"
                          placeholder="e.g. 26.9124336"
                          value={editFormData.PickupLatitude ?? ""}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              PickupLatitude: e.target.value,
                            })
                          }
                          onBlur={() => {
                            if (
                              editFormData.PickupLatitude &&
                              editFormData.PickupLongitude &&
                              editFormData.Stop1Latitude &&
                              editFormData.Stop1Longitude
                            ) {
                              triggerRouteDistanceAndFareCheck(
                                editFormData.PickupLatitude,
                                editFormData.PickupLongitude,
                                editFormData.Stop1Latitude,
                                editFormData.Stop1Longitude
                              )
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          <i className="fas fa-compass me-1 text-danger"></i>{" "}
                          Pickup Longitude
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="any"
                          placeholder="e.g. 75.7872709"
                          value={editFormData.PickupLongitude ?? ""}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              PickupLongitude: e.target.value,
                            })
                          }
                          onBlur={() => {
                            if (
                              editFormData.PickupLatitude &&
                              editFormData.PickupLongitude &&
                              editFormData.Stop1Latitude &&
                              editFormData.Stop1Longitude
                            ) {
                              triggerRouteDistanceAndFareCheck(
                                editFormData.PickupLatitude,
                                editFormData.PickupLongitude,
                                editFormData.Stop1Latitude,
                                editFormData.Stop1Longitude
                              )
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-muted">
                      Pickup Full Address
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editFormData.PickupAddress}
                      onChange={e =>
                        setEditFormData({
                          ...editFormData,
                          PickupAddress: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Sender Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={editFormData.SenderName}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              SenderName: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Sender Mobile
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={editFormData.SenderMobile}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              SenderMobile: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* SECTION 4: DROP DETAILS */}
                <div className="p-3 rounded mb-1 bg-light border shadow-sm">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="fw-bold text-dark mb-0">
                      <i className="fas fa-flag-checkered me-1 text-success"></i>{" "}
                      Drop (Stop 1) Location & Contact
                    </h6>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        type="button"
                        className="py-1 px-2"
                        onClick={() => handleDetectCurrentLocation("drop")}
                        title="Detect device GPS"
                      >
                        <i className="fas fa-crosshairs me-1"></i> My GPS
                      </Button>
                      {editFormData.Stop1Latitude &&
                        editFormData.Stop1Longitude && (
                          <a
                            href={`https://www.google.com/maps?q=${editFormData.Stop1Latitude},${editFormData.Stop1Longitude}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-secondary py-1 px-2"
                            title="Open in Google Maps"
                          >
                            <i className="fas fa-external-link-alt me-1"></i>{" "}
                            View Map
                          </a>
                        )}
                    </div>
                  </div>

                  {/* Search with Google Places Autocomplete */}
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-muted mb-1">
                      <i className="fab fa-google text-primary me-1"></i> Search
                      Drop Location (Google Places Autocomplete)
                    </Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fas fa-search text-muted"></i>
                      </span>
                      <input
                        ref={dropSearchInputRef}
                        type="text"
                        className="form-control"
                        placeholder="Type location to auto-search & populate coordinates..."
                      />
                    </div>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      Selecting a place automatically updates the address and
                      exact GPS coordinates below.
                    </small>
                  </Form.Group>

                  {/* Direct Latitude & Longitude Inputs */}
                  <Row className="g-2 mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          <i className="fas fa-compass me-1 text-success"></i>{" "}
                          Drop Latitude
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="any"
                          placeholder="e.g. 26.8524336"
                          value={editFormData.Stop1Latitude ?? ""}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              Stop1Latitude: e.target.value,
                            })
                          }
                          onBlur={() => {
                            if (
                              editFormData.PickupLatitude &&
                              editFormData.PickupLongitude &&
                              editFormData.Stop1Latitude &&
                              editFormData.Stop1Longitude
                            ) {
                              triggerRouteDistanceAndFareCheck(
                                editFormData.PickupLatitude,
                                editFormData.PickupLongitude,
                                editFormData.Stop1Latitude,
                                editFormData.Stop1Longitude
                              )
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          <i className="fas fa-compass me-1 text-success"></i>{" "}
                          Drop Longitude
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="any"
                          placeholder="e.g. 75.8272709"
                          value={editFormData.Stop1Longitude ?? ""}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              Stop1Longitude: e.target.value,
                            })
                          }
                          onBlur={() => {
                            if (
                              editFormData.PickupLatitude &&
                              editFormData.PickupLongitude &&
                              editFormData.Stop1Latitude &&
                              editFormData.Stop1Longitude
                            ) {
                              triggerRouteDistanceAndFareCheck(
                                editFormData.PickupLatitude,
                                editFormData.PickupLongitude,
                                editFormData.Stop1Latitude,
                                editFormData.Stop1Longitude
                              )
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-muted">
                      Drop Full Address
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editFormData.Stop1Address}
                      onChange={e =>
                        setEditFormData({
                          ...editFormData,
                          Stop1Address: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Receiver Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={editFormData.Stop1ReceiverName}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              Stop1ReceiverName: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-muted">
                          Receiver Mobile
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={editFormData.Stop1ReceiverMobile}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              Stop1ReceiverMobile: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowEditModal(false)}
              disabled={isSavingEdit}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              type="submit"
              disabled={isSavingEdit}
              className="text-dark fw-bold"
            >
              {isSavingEdit ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-1"
                  />
                  Saving Changes...
                </>
              ) : (
                "Save Booking Changes"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ============================================================== */}
      {/* 3. PHOTO LIGHTBOX & UPLOAD / REPLACE MODAL                       */}
      {/* ============================================================== */}
      <Modal
        show={showPhotoModal}
        onHide={() => setShowPhotoModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom pb-3">
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                backgroundColor: "#e0f2fe",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0284c7",
                fontSize: "1.2rem",
              }}
            >
              <i className="fas fa-camera"></i>
            </span>
            <div>
              <Modal.Title className="h5 mb-0 fw-bold">
                Pickup & Drop Photos · Booking #{photoBooking?.Id}
              </Modal.Title>
              <small className="text-muted">
                Inspect proof of pickup and proof of delivery, or upload/replace
                them directly.
              </small>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {photoBooking && (
            <div>
              {/* Photo selector buttons */}
              <div className="d-flex gap-2 mb-3 border-bottom pb-2">
                <Button
                  variant={
                    activePhotoTab === "pickup" ? "primary" : "outline-primary"
                  }
                  onClick={() => setActivePhotoTab("pickup")}
                  className="d-flex align-items-center gap-1.5"
                >
                  <i className="fas fa-camera"></i>
                  <span>
                    Pickup Photo{" "}
                    {getPickupPhoto(photoBooking) ? "✓" : "(Missing)"}
                  </span>
                </Button>
                <Button
                  variant={
                    activePhotoTab === "drop" ? "success" : "outline-success"
                  }
                  onClick={() => setActivePhotoTab("drop")}
                  className="d-flex align-items-center gap-1.5"
                >
                  <i className="fas fa-flag-checkered"></i>
                  <span>
                    Drop Delivery Photo{" "}
                    {getDropPhoto(photoBooking) ? "✓" : "(Missing)"}
                  </span>
                </Button>
              </div>

              {/* ACTIVE PHOTO DISPLAY */}
              {activePhotoTab === "pickup" ? (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold text-primary mb-0">
                      📸 Pickup Photo (Goods Loaded at Origin)
                    </h6>
                    {getPickupPhoto(photoBooking) && (
                      <a
                        href={buildImageUrl(getPickupPhoto(photoBooking))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="fas fa-external-link-alt me-1"></i> Open
                        Original
                      </a>
                    )}
                  </div>

                  {getPickupPhoto(photoBooking) ? (
                    <div
                      className="text-center p-2 bg-dark rounded mb-3"
                      style={{ maxHeight: "420px", overflow: "hidden" }}
                    >
                      <img
                        src={buildImageUrl(getPickupPhoto(photoBooking))}
                        alt="Pickup Photo"
                        onError={e => {
                          e.currentTarget.onerror = null
                          e.currentTarget.style.display = "none"
                        }}
                        style={{
                          maxHeight: "400px",
                          maxWidth: "100%",
                          objectFit: "contain",
                          borderRadius: 4,
                        }}
                      />
                    </div>
                  ) : (
                    <div className="alert alert-warning text-center py-4 mb-3">
                      <i className="fas fa-image fa-2x mb-2 text-warning d-block"></i>
                      <strong>No separate pickup photo uploaded.</strong>
                      <p className="small text-muted mb-2">
                        Driver did not upload a separate pickup photo at origin.
                      </p>
                      {photoBooking.Stop1Photo && (
                        <div className="mt-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setActivePhotoTab("drop")}
                          >
                            <i className="fas fa-eye me-1"></i> View Available
                            Trip / Stop 1 Photo
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Upload / Replace Pickup Photo */}
                  <div className="border rounded p-3 bg-light">
                    <Form.Label className="fw-semibold text-dark mb-1">
                      <i className="fas fa-upload me-1 text-primary"></i> Upload
                      or Replace Pickup Photo:
                    </Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        disabled={isUploadingPhoto}
                        onChange={e => {
                          if (e.target.files && e.target.files[0]) {
                            handleUploadPhoto("pickup", e.target.files[0])
                          }
                        }}
                      />
                      {isUploadingPhoto && (
                        <Spinner
                          animation="border"
                          size="sm"
                          variant="primary"
                        />
                      )}
                    </div>
                    <small className="text-muted">
                      Supports JPG, PNG, WEBP. Photo will be automatically saved
                      and optimized.
                    </small>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold text-success mb-0">
                      🏁 Drop Photo (Delivered at Destination)
                    </h6>
                    {getDropPhoto(photoBooking) && (
                      <a
                        href={buildImageUrl(getDropPhoto(photoBooking))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-success"
                      >
                        <i className="fas fa-external-link-alt me-1"></i> Open
                        Original
                      </a>
                    )}
                  </div>

                  {getDropPhoto(photoBooking) ? (
                    <div
                      className="text-center p-2 bg-dark rounded mb-3"
                      style={{ maxHeight: "420px", overflow: "hidden" }}
                    >
                      <img
                        src={buildImageUrl(getDropPhoto(photoBooking))}
                        alt="Drop Delivery Photo"
                        onError={e => {
                          e.currentTarget.onerror = null
                          e.currentTarget.style.display = "none"
                        }}
                        style={{
                          maxHeight: "400px",
                          maxWidth: "100%",
                          objectFit: "contain",
                          borderRadius: 4,
                        }}
                      />
                    </div>
                  ) : (
                    <div className="alert alert-warning text-center py-4 mb-3">
                      <i className="fas fa-image fa-2x mb-2 text-warning d-block"></i>
                      <strong>No drop delivery photo uploaded yet.</strong>
                      <p className="small text-muted mb-0">
                        This trip has not reached delivery status or driver did
                        not upload drop proof.
                      </p>
                    </div>
                  )}

                  {/* Upload / Replace Drop Photo */}
                  <div className="border rounded p-3 bg-light">
                    <Form.Label className="fw-semibold text-dark mb-1">
                      <i className="fas fa-upload me-1 text-success"></i> Upload
                      or Replace Drop Photo:
                    </Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        disabled={isUploadingPhoto}
                        onChange={e => {
                          if (e.target.files && e.target.files[0]) {
                            handleUploadPhoto("drop", e.target.files[0])
                          }
                        }}
                      />
                      {isUploadingPhoto && (
                        <Spinner
                          animation="border"
                          size="sm"
                          variant="success"
                        />
                      )}
                    </div>
                    <small className="text-muted">
                      Supports JPG, PNG, WEBP. Photo will be saved as delivery
                      proof.
                    </small>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPhotoModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============================================================== */}
      {/* 4. ADMIN QUICK STATUS CHANGER MODAL                              */}
      {/* ============================================================== */}
      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-bottom pb-3">
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                backgroundColor: "#e0e7ff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#4f46e5",
                fontSize: "1.2rem",
              }}
            >
              <i className="fas fa-tasks"></i>
            </span>
            <div>
              <Modal.Title className="h5 mb-0 fw-bold">
                Change Booking Status
              </Modal.Title>
              <small className="text-muted">
                Booking #{statusBooking?.Id} · Current: {statusBooking?.Status}
              </small>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {statusBooking && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  Select New Status
                </Form.Label>
                <Form.Select
                  size="lg"
                  value={newStatusId}
                  onChange={e => setNewStatusId(e.target.value)}
                  className="fw-bold"
                >
                  {ALL_BOOKING_STATUSES.map(st => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {newStatusId === "19" && (
                <div className="alert alert-danger py-2 px-3 small">
                  <strong>⚠️ Warning:</strong> Cancelling this booking will
                  terminate the ride and notify both driver and customer.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowStatusModal(false)}
            disabled={isUpdatingStatus}
          >
            Cancel
          </Button>
          <Button
            variant={newStatusId === "19" ? "danger" : "primary"}
            onClick={handleConfirmStatusChange}
            disabled={isUpdatingStatus}
          >
            {isUpdatingStatus ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Updating Status...
              </>
            ) : (
              "Confirm Status Change"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Live Trip Tracking Modal */}
      <LiveTripTrackingModal
        isOpen={showLiveTrackModal}
        toggle={() => setShowLiveTrackModal(prev => !prev)}
        booking={liveTrackBooking}
      />
    </div>
  )
}

export default AssignDriver
